import React, { useState, useEffect } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { toast } from "sonner";
import { Search, Globe, Image, Tag } from "lucide-react";

const ManageSEO = () => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    about_title: "",
    about_content: "",
    contact_email: "",
    contact_phone: "",
    contact_address: "",
    social_facebook: "",
    social_instagram: "",
    social_twitter: "",
    seo_title: "",
    seo_description: "",
    seo_keywords: "",
    seo_og_image: "",
  });

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/settings`);
      setFormData(response.data);
    } catch (error) {
      console.error("Error fetching settings:", error);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const token = localStorage.getItem("adminToken");
    const headers = { Authorization: `Bearer ${token}` };

    try {
      await axios.post(`${API}/settings`, formData, { headers });
      toast.success("SEO settings updated successfully!");
    } catch (error) {
      console.error("Error updating settings:", error);
      toast.error("Failed to update SEO settings");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="manage-seo">
      <h1 className="text-3xl font-bold text-gray-900 mb-2">SEO Settings</h1>
      <p className="text-gray-600 mb-8">
        Optimize your website for search engines and social media sharing
      </p>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Meta Tags */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <Search className="mr-2 text-[#8B7355]" size={24} />
              <div>
                <CardTitle>Meta Tags</CardTitle>
                <CardDescription>Control how your site appears in search results</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="seo_title">Site Title *</Label>
              <Input
                id="seo_title"
                name="seo_title"
                data-testid="seo-title-input"
                value={formData.seo_title}
                onChange={handleChange}
                placeholder="Mobile Bar Service & Rental | Mélange Mobile Bar"
                maxLength={60}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.seo_title?.length || 0}/60 characters (optimal: 50-60)
              </p>
            </div>
            <div>
              <Label htmlFor="seo_description">Meta Description *</Label>
              <Textarea
                id="seo_description"
                name="seo_description"
                rows={3}
                data-testid="seo-description-input"
                value={formData.seo_description}
                onChange={handleChange}
                placeholder="Bartending That Comes To You — Book a mobile bar for weddings, parties, or private events..."
                maxLength={160}
                required
              />
              <p className="text-xs text-gray-500 mt-1">
                {formData.seo_description?.length || 0}/160 characters (optimal: 150-160)
              </p>
            </div>
            <div>
              <Label htmlFor="seo_keywords">Keywords</Label>
              <Input
                id="seo_keywords"
                name="seo_keywords"
                data-testid="seo-keywords-input"
                value={formData.seo_keywords}
                onChange={handleChange}
                placeholder="mobile bar, bartending service, cocktail catering, wedding bar"
              />
              <p className="text-xs text-gray-500 mt-1">
                Separate keywords with commas. Focus on 5-10 relevant keywords.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Open Graph / Social Media */}
        <Card>
          <CardHeader>
            <div className="flex items-center">
              <Globe className="mr-2 text-[#8B7355]" size={24} />
              <div>
                <CardTitle>Open Graph / Social Media</CardTitle>
                <CardDescription>Control how your site appears when shared on social media</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label htmlFor="seo_og_image">Social Share Image URL</Label>
              <Input
                id="seo_og_image"
                name="seo_og_image"
                data-testid="seo-og-image-input"
                value={formData.seo_og_image}
                onChange={handleChange}
                placeholder="https://example.com/melange-share-image.jpg"
              />
              <p className="text-xs text-gray-500 mt-1">
                Recommended size: 1200x630px. Used for Facebook, Twitter, LinkedIn previews.
              </p>
            </div>
            {formData.seo_og_image && (
              <div className="border rounded-lg p-4">
                <Label className="mb-2 block">Preview:</Label>
                <img 
                  src={formData.seo_og_image} 
                  alt="OG Preview" 
                  className="max-w-md w-full h-auto rounded"
                  onError={(e) => {
                    e.target.style.display = 'none';
                  }}
                />
              </div>
            )}
          </CardContent>
        </Card>

        {/* SEO Tips */}
        <Card className="bg-[#F7F4F0]">
          <CardHeader>
            <div className="flex items-center">
              <Tag className="mr-2 text-[#8B7355]" size={24} />
              <CardTitle>SEO Best Practices</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            <ul className="space-y-2 text-sm text-gray-700">
              <li>✓ <strong>Title:</strong> Include your main keyword and brand name (50-60 chars)</li>
              <li>✓ <strong>Description:</strong> Write compelling copy with call-to-action (150-160 chars)</li>
              <li>✓ <strong>Keywords:</strong> Focus on long-tail keywords your customers search for</li>
              <li>✓ <strong>Social Image:</strong> Use high-quality images with your logo/branding</li>
              <li>✓ <strong>Update Regularly:</strong> Refresh content to maintain search rankings</li>
            </ul>
          </CardContent>
        </Card>

        <Button
          type="submit"
          disabled={loading}
          className="btn-primary"
          data-testid="seo-submit-button"
        >
          {loading ? "Saving..." : "Save SEO Settings"}
        </Button>
      </form>
    </div>
  );
};

export default ManageSEO;
